import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '../../components/MainLayout';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import {
  UserCog,
  Plus,
  Search,
  Pencil,
  Trash2,
  Shield,
  User as UserIcon,
  Car,
  Users,
} from 'lucide-react';
import api from '../../lib/api';
import { UserDialog } from './UserDialog';

interface UserItem {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  role: string;
  is_active: boolean;
  membership_role: string;
  created_at: string;
}

interface UsersResponse {
  users: UserItem[];
  total: number;
  admins_count: number;
  users_count: number;
  vehicles_count: number;
  customers_count: number;
  max_admins: number;
  max_users: number;
  max_vehicles: number;
  max_customers: number;
}

function PlanLimitBar({
  used,
  max,
  label,
  icon: Icon,
}: {
  used: number;
  max: number;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  const pct = max > 0 ? Math.min(100, (used / max) * 100) : 0;
  const color = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-orange-400' : 'bg-primary';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Icon className="h-4 w-4" />
          <span>{label}</span>
        </div>
        <span className={`font-medium tabular-nums ${pct >= 100 ? 'text-red-600' : ''}`}>
          {used} / {max}
        </span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function UsersPage() {
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserItem | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<UsersResponse>({
    queryKey: ['users', search],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const res = await api.get(`/users?${params}`);
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({ title: 'Benutzer entfernt' });
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: (isAxiosError(error) ? error.response?.data?.detail : undefined) || 'Benutzer konnte nicht entfernt werden',
      });
    },
  });

  const handleOpenCreate = () => {
    setEditingUser(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (user: UserItem) => {
    setEditingUser(user);
    setDialogOpen(true);
  };

  const handleDelete = (user: UserItem) => {
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;
    if (window.confirm(`Benutzer "${name}" aus diesem Mandanten entfernen?`)) {
      deleteMutation.mutate(user.id);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <UserCog className="h-7 w-7" />
            Benutzerverwaltung
          </h1>
          <Button onClick={handleOpenCreate}>
            <Plus className="w-4 h-4 mr-2" />
            Neuer Benutzer
          </Button>
        </div>

        {/* Plan limits */}
        {data && (
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="font-display text-lg">Plan-Nutzung</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                <PlanLimitBar
                  used={data.admins_count}
                  max={data.max_admins}
                  label="Admins"
                  icon={Shield}
                />
                <PlanLimitBar
                  used={data.users_count}
                  max={data.max_users}
                  label="Benutzer"
                  icon={UserIcon}
                />
                <PlanLimitBar
                  used={data.vehicles_count}
                  max={data.max_vehicles}
                  label="Fahrzeuge"
                  icon={Car}
                />
                <PlanLimitBar
                  used={data.customers_count}
                  max={data.max_customers}
                  label="Kunden"
                  icon={Users}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="font-display text-lg">Suche</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Suchen nach Name oder E-Mail..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Users list */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="font-display">Benutzer{data ? ` (${data.total})` : ''}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : !data || data.users.length === 0 ? (
              <div className="text-center py-12">
                <UserCog className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Keine Benutzer gefunden</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Benutzer
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase hidden sm:table-cell">
                      Rolle
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase hidden sm:table-cell">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.users.map((u) => {
                    const displayName =
                      [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
                    return (
                      <tr key={u.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {u.membership_role === 'admin' ? (
                              <Shield className="h-4 w-4 text-primary flex-shrink-0" />
                            ) : (
                              <UserIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            )}
                            <div>
                              <div className="font-medium text-sm">{displayName}</div>
                              <div className="text-xs text-muted-foreground">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span
                            className={`px-2 py-0.5 rounded-pill text-xs font-medium ${
                              u.membership_role === 'admin'
                                ? 'bg-blue-500/15 text-blue-600 dark:text-blue-400'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {u.membership_role === 'admin' ? 'Admin' : 'Benutzer'}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span
                            className={`px-2 py-0.5 rounded-pill text-xs font-medium ${
                              u.is_active
                                ? 'bg-green-500/15 text-green-600 dark:text-green-400'
                                : 'bg-red-500/15 text-red-600 dark:text-red-400'
                            }`}
                          >
                            {u.is_active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(u)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(u)}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      <UserDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        user={editingUser}
      />
    </MainLayout>
  );
}
